import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Rating,
  Slider,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
} from '@mui/material';

import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import DifficultyGauge from './DifficultyGauge';
import api, { fetchSemesters, fetchMyReviews } from '../services/api';
import { useAuth } from './AuthContext';
import { useInstitution } from './InstitutionContext';

const AddReviewDialog = ({
  open,
  onClose,
  departmentId,
  courseId,
  professorCourseId,
  departmentName,
  courseName,
  courseShortName,
  professorName,
  onAdded,
  onSuccess,
}) => {
  const [term, setTerm] = useState('Spring');
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(String(currentYear));
  const [rating, setRating] = useState(0);
  const [difficulty, setDifficulty] = useState(0);
  const [gradeReceived, setGradeReceived] = useState('');
  const [agreeToShowGrade, setAgreeToShowGrade] = useState(false);
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { user } = useAuth();
  const { selectedInstitution } = useInstitution();

  const gradeMarks = [
    { value: 4.0, label: 'A' },
    { value: 3.7, label: 'A-' },
    { value: 3.3, label: 'B+' },
    { value: 3.0, label: 'B' },
    { value: 2.7, label: 'B-' },
    { value: 2.3, label: 'C+' },
    { value: 2.0, label: 'C' },
    { value: 1.7, label: 'C-' },
    { value: 1.3, label: 'D+' },
    { value: 1.0, label: 'D' },
    { value: 0.7, label: 'D-' },
    { value: 0.0, label: 'F' },
  ];


  useEffect(() => {
    if (!open) return;
    setTerm('Spring');
    setYear(String(currentYear));
    setRating(0);
    setDifficulty(0);
    setGradeReceived('');
    setAgreeToShowGrade(false);
    setContent('');
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!term || !year || !rating || !content) return;
    setSubmitting(true);
    try {
      let semId;
      try {
        const semRes = await api.post('/courses/semesters/', {
          term,
          year: parseInt(year, 10),
        });
        semId = semRes.data.id;
      } catch (err) {
        if (err.response && err.response.status === 400) {
          try {
            const data = await fetchSemesters();
            const list = data.results || data;
            const existing = list.find(
              (s) => s.term === term && s.year === parseInt(year, 10)
            );
            if (existing) {
              semId = existing.id;
            } else {
              throw err;
            }
          } catch (fetchErr) {
            throw fetchErr;
          }
        } else {
          throw err;
        }
      }

      const payload = {
        professor_course: professorCourseId,
        semester: semId,
        content,
        rating,
        difficulty_level: parseInt(difficulty, 10),
        grade_received:
          agreeToShowGrade && gradeReceived !== ''
            ? parseFloat(gradeReceived)
            : null,
      };
      const res = await api.post('/courses/reviews/', payload);
      const newReview = res.data;
      let isFirstReview = false;
      try {
        const instId = selectedInstitution || user?.userprofile?.institution?.id;
        const mine = await fetchMyReviews(instId);
        const list = mine.results || mine;
        if (Array.isArray(list) && list.length === 1) {
          isFirstReview = true;
        }
      } catch (err) {
        console.error('Failed to fetch my reviews:', err.response?.data || err.message);
      }
      if (onAdded) await onAdded(newReview, isFirstReview);
      const msg = 'Review added';
      if (onSuccess) onSuccess(msg); else setSuccessMessage(msg);
      onClose();
      return newReview;
    } catch (err) {
      console.error('Failed to add review:', err.response?.data || err.message);
    } finally {
      setSubmitting(false);
    }
  };



  const displayCourse = courseShortName
    ? `${courseShortName} - ${courseName}`
    : courseName;

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ component: 'form', onSubmit: handleSubmit }}
    >
      <DialogTitle>Add Review</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Box>
          <Typography variant="body2">{departmentName}</Typography>
        </Box>
        <Box>
          <Typography variant="body2">{displayCourse}</Typography>
        </Box>
        <Box>
          <Typography variant="body2">{professorName}</Typography>
        </Box>
        <FormControl fullWidth>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            Semester <span style={{ color: 'red' }}>*</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <Select value={term} onChange={(e) => setTerm(e.target.value)}>
                <MenuItem value="Spring">Spring</MenuItem>
                <MenuItem value="Summer">Summer</MenuItem>
                <MenuItem value="Fall">Fall</MenuItem>
                <MenuItem value="Winter">Winter</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 100 }} size="small">
              <Select value={year} onChange={(e) => setYear(e.target.value)}>
                {[...Array(10)].map((_, idx) => (
                  <MenuItem key={idx} value={String(currentYear - idx)}>
                    {currentYear - idx}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </FormControl>
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            Rating <span style={{ color: 'red' }}>*</span>
          </Typography>
          <Rating
            name="rating"
            value={rating}
            onChange={(e, newValue) => setRating(newValue)}
            precision={1}
            max={5}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: -0.5 }}>
            Difficulty Level <span style={{ color: 'red' }}>*</span>
          </Typography>
          <DifficultyGauge
            value={difficulty}
            onChange={setDifficulty}
            size={100}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            Grade Received <span style={{ opacity: 0.6 }}>(optional)</span>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Slider
              min={0}
              max={4}
              step={0.1}
              marks={gradeMarks}
              value={gradeReceived === '' ? 0 : parseFloat(gradeReceived)}
              onChange={(e, val) => setGradeReceived(val.toFixed(1))}
              valueLabelDisplay="auto"
              disabled={!agreeToShowGrade}
              sx={{ flexGrow: 1 }}
            />
            <TextField
              value={gradeReceived}
              onChange={(e) => setGradeReceived(e.target.value)}
              size="small"
              type="number"
              disabled={!agreeToShowGrade}
              inputProps={{ min: 0, max: 4, step: 0.1 }}
              sx={{ width: 80 }}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreeToShowGrade}
                onChange={(e) => {
                  setAgreeToShowGrade(e.target.checked);
                  if (!e.target.checked) setGradeReceived('');
                }}
              />
            }
            label="Display grade with review"
          />
        </Box>
        <div>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            Review Content <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            multiline
            rows={4}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            fullWidth
          />
        </div>
      </DialogContent>
      <DialogActions>
        <OutlinedButton onClick={onClose} text="Cancel" color="info" />
        <FilledButton
          type="submit"
          disabled={
            submitting ||
            !term ||
            !year ||
            rating === 0 ||
            !content
          }
          text="Submit"
          color="info"
        />
      </DialogActions>
    </Dialog>
    <Snackbar
      open={!!successMessage}
      autoHideDuration={3000}
      onClose={() => setSuccessMessage('')}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%', fontWeight: 'bold' }}>
        {successMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default AddReviewDialog;
